import React from 'react';

export interface EnhancedBeltProps {
  /**
   * Base belt color (white, blue, purple, brown, black, etc.)
   */
  color: string;
  
  /**
   * Number of stripes on the belt (0-10)
   */
  stripes?: number;
  
  /**
   * Optional contrast color for patterns
   */
  contrastColor?: string;
  
  /**
   * Contrast pattern type
   */
  contrastType?: 'alternate' | 'centered horizontal stripe' | 'top horizontal stripe' | '2x horizontal stripes' | 'character' | 'none';
  
  /**
   * Belt level (junior or adult)
   */
  level?: 'junior' | 'adult';
  
  /**
   * Stripe color (defaults to white)
   */
  stripeColor?: string;
  
  /**
   * Background color for stripe area
   */
  stripeBgColor?: string;
  
  /**
   * Optional custom classNames for styling
   */
  className?: string;
  
  /**
   * Whether to show the belt component
   */
  visible?: boolean;
  
  /**
   * Optional size variant
   */
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  
  /**
   * Belt height override
   */
  height?: string;
  
  /**
   * Show border for white belts
   */
  showBorder?: boolean;
}

// Color definitions matching original Marune app
const BELT_COLORS: Record<string, string> = {
  white: '#ffffff',
  yellow: '#ffff00',
  orange: '#ff8c00',
  green: '#2eff00',
  blue: '#0062ff',
  red: '#ff0000',
  purple: '#cb00ff',
  brown: '#775300',
  black: '#000000',
  grey: '#808080',
  gray: '#808080'
};

/**
 * Enhanced Belt component that displays martial arts belts with advanced features
 * 
 * Supports:
 * - All standard belt colors with up to 10 stripes
 * - Contrast patterns (alternate blocks, horizontal stripes)
 * - Junior/adult belt variations
 * - Custom stripe colors and backgrounds
 * - Border treatment for white belts
 */
const EnhancedBelt: React.FC<EnhancedBeltProps> = ({
  color = 'white',
  stripes = 0,
  contrastColor,
  contrastType = 'none',
  level = 'adult',
  stripeColor = 'white',
  stripeBgColor,
  className = '',
  visible = true,
  size = 'medium',
  height,
  // showBorder is kept for API compatibility but border is now always shown
  showBorder: _showBorder = true,
}) => {
  // Don't render if not visible
  if (!visible) return null;

  // Normalize color to lowercase for lookups
  const normalizedColor = color.toLowerCase();
  const baseColorHex = BELT_COLORS[normalizedColor] || BELT_COLORS.white;
  const contrastColorHex = contrastColor ? BELT_COLORS[contrastColor.toLowerCase()] || BELT_COLORS.white : '';
  const stripeColorHex = BELT_COLORS[stripeColor.toLowerCase()] || BELT_COLORS.white;
  
  // Determine stripe background color
  let stripeBgHex = stripeBgColor ? BELT_COLORS[stripeBgColor.toLowerCase()] || '#808080' : '';
  if (!stripeBgHex) {
    // Default stripe backgrounds
    if (normalizedColor === 'black') {
      stripeBgHex = '#ff0000'; // Red background for black belt stripes
    } else if (normalizedColor === 'white') {
      stripeBgHex = '#000000'; // Black background for white belt stripes
    } else {
      stripeBgHex = '#000000'; // Default black background
    }
  }

  // Sizes for different variants
  const sizeConfigs = {
    small: { height: '12px', minWidth: '120px', stripeWidth: '3px' },
    medium: { height: '16px', minWidth: '160px', stripeWidth: '4px' },
    large: { height: '20px', minWidth: '200px', stripeWidth: '5px' },
    xlarge: { height: '28px', minWidth: '240px', stripeWidth: '6px' }
  };

  const config = sizeConfigs[size];
  const effectiveHeight = height || config.height;

  // Limit stripes to a maximum of 10
  const validStripes = Math.min(Math.max(0, stripes), 10);

  // Render contrast pattern
  const renderContrastPattern = () => {
    if (contrastType === 'none' || !contrastColorHex) return null;

    switch (contrastType) {
      case 'alternate':
        // Render alternating blocks
        return (
          <div className="absolute inset-0 flex">
            {[0, 1, 2, 3].map((index) => (
              <div
                key={`contrast-block-${index}`}
                className="flex-shrink-0"
                style={{
                  backgroundColor: contrastColorHex,
                  width: '12.5%',
                  marginLeft: '12.5%',
                  height: '100%'
                }}
              />
            ))}
          </div>
        );

      case 'centered horizontal stripe':
        return (
          <div
            className="absolute left-0 right-0"
            style={{
              backgroundColor: contrastColorHex,
              height: '30%',
              top: '35%'
            }}
          />
        );

      case 'top horizontal stripe':
        return (
          <div
            className="absolute left-0 right-0 top-0"
            style={{
              backgroundColor: contrastColorHex,
              height: '50%'
            }}
          />
        );

      case '2x horizontal stripes':
        return (
          <>
            <div
              className="absolute left-0 right-0"
              style={{
                backgroundColor: contrastColorHex,
                height: '20%',
                top: '20%'
              }}
            />
            <div
              className="absolute left-0 right-0"
              style={{
                backgroundColor: contrastColorHex,
                height: '20%',
                bottom: '20%'
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  // Junior belt modifier
  const isJunior = level === 'junior';

  return (
    <div
      className={`relative flex rounded-sm overflow-hidden ${className}`}
      style={{
        height: effectiveHeight,
        minWidth: config.minWidth,
        // Always show a thin black border for definition, especially for white belts on light backgrounds
        border: '1px solid #1f2937'
      }}
      aria-label={`${color} belt with ${validStripes} stripes`}
    >
      {/* Main belt background */}
      <div 
        className="flex-grow relative"
        style={{ backgroundColor: baseColorHex }}
      >
        {/* Junior belt indicator - white stripe through middle */}
        {isJunior && (
          <div
            className="absolute left-0 right-0"
            style={{
              backgroundColor: '#ffffff',
              height: '30%',
              top: '35%'
            }}
          />
        )}

        {/* Contrast pattern overlay */}
        {renderContrastPattern()}

        {/* Stripes section */}
        {validStripes > 0 && (
          <div 
            className="absolute right-0 top-0 bottom-0 flex items-center justify-end px-1"
            style={{ 
              backgroundColor: stripeBgHex,
              minWidth: validStripes * 8 + 16,
              paddingRight: '6px',
              paddingLeft: '4px'
            }}
          >
            {/* Generate individual stripes */}
            {Array.from({ length: validStripes }).map((_, index) => (
              <div
                key={`stripe-${index}`}
                className="flex-shrink-0"
                style={{
                  backgroundColor: stripeColorHex,
                  width: config.stripeWidth,
                  height: '60%',
                  marginLeft: index > 0 ? '4px' : '0'
                }}
                aria-hidden="true"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedBelt;